import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Skeleton,
    Alert,
    Collapse,
    Grid,
    Divider,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import orderService, { Order, OrderStatus, PaymentStatus } from '../../services/orderService';
import { useTranslation } from '../../i18n';

const MyOrders: React.FC = () => {
    const { t } = useTranslation();
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await orderService.getOrders();
            setOrders(data);
        } catch (err) {
            console.error('Error loading orders:', err);
            setError(t('orders.loading'));
        } finally {
            setLoading(false);
        }
    };

    const handleViewOrder = (order: Order) => {
        setSelectedOrder(order);
    };

    const handleCloseDialog = () => {
        setSelectedOrder(null);
    };

    const toggleExpand = (orderId: number) => {
        setExpandedOrder(expandedOrder === orderId ? null : orderId);
    };

    const getStatusChip = (status: OrderStatus) => (
        <Chip
            label={orderService.getStatusLabel(status)}
            color={orderService.getStatusColor(status)}
            size="small"
        />
    );

    const getPaymentStatusChip = (status: PaymentStatus) => (
        <Chip
            label={orderService.getPaymentStatusLabel(status)}
            color={orderService.getPaymentStatusColor(status)}
            size="small"
            variant="outlined"
        />
    );

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon color="primary" />
                    {t('orders.title')}
                </Typography>
                {[1, 2, 3].map((i) => (
                    <Card key={i} sx={{ mb: 2 }}>
                        <CardContent>
                            <Skeleton variant="text" width="30%" height={32} />
                            <Skeleton variant="text" width="50%" />
                            <Skeleton variant="text" width="40%" />
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon color="primary" />
                {t('orders.title')}
            </Typography>

            {orders.length === 0 ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {t('orders.noOrders')}
                </Alert>
            ) : (
                <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width={40}></TableCell>
                                <TableCell>{t('orders.orderNumber')}</TableCell>
                                <TableCell>{t('orders.orderDate')}</TableCell>
                                <TableCell align="center">{t('orders.items')}</TableCell>
                                <TableCell align="right">{t('orders.total')}</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="center" width={80}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {orders.map((order) => (
                                <React.Fragment key={order.id}>
                                    <TableRow
                                        hover
                                        sx={{ '& > *': { borderBottom: expandedOrder === order.id ? 'none' : undefined } }}
                                    >
                                        <TableCell>
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpand(order.id)}
                                            >
                                                {expandedOrder === order.id
                                                    ? <ExpandLessIcon />
                                                    : <ExpandMoreIcon />
                                                }
                                            </IconButton>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {order.orderNumber}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {orderService.formatDate(order.createdAt)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {orderService.getItemCount(order)}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography fontWeight="medium">
                                                {orderService.formatTotal(order)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {getStatusChip(order.status)}
                                                {getPaymentStatusChip(order.paymentStatus)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewOrder(order)}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={7} sx={{ py: 0 }}>
                                            <Collapse in={expandedOrder === order.id} timeout="auto" unmountOnExit>
                                                <Box sx={{ py: 2, px: 4, bgcolor: 'action.hover' }}>
                                                    <Typography variant="subtitle2" gutterBottom>
                                                        Bestellte Artikel:
                                                    </Typography>
                                                    {order.items.map((item, index) => (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                py: 0.5,
                                                            }}
                                                        >
                                                            <Typography variant="body2">
                                                                {item.title}
                                                                {item.quantity > 1 && ` x${item.quantity}`}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {parseFloat(item.total) === 0
                                                                    ? 'Kostenlos'
                                                                    : orderService.formatTotal({ ...order, total: item.total })
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Order Detail Dialog */}
            <Dialog
                open={!!selectedOrder}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
            >
                {selectedOrder && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6">
                                {t('orders.detail.title')} - {selectedOrder.orderNumber}
                            </Typography>
                            <IconButton onClick={handleCloseDialog}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        {t('orders.detail.customerInfo')}
                                    </Typography>
                                    <Typography variant="body1">{selectedOrder.customerName}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedOrder.customerEmail}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {getStatusChip(selectedOrder.status)}
                                        {getPaymentStatusChip(selectedOrder.paymentStatus)}
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        {t('orders.detail.orderItems')}
                                    </Typography>
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Produkt</TableCell>
                                                    <TableCell align="right">Preis</TableCell>
                                                    <TableCell align="center">Menge</TableCell>
                                                    <TableCell align="right">Summe</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {selectedOrder.items.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>{item.title}</TableCell>
                                                        <TableCell align="right">
                                                            {orderService.formatTotal({ ...selectedOrder, total: item.unitPrice })}
                                                        </TableCell>
                                                        <TableCell align="center">{item.quantity}</TableCell>
                                                        <TableCell align="right">
                                                            {orderService.formatTotal({ ...selectedOrder, total: item.total })}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Box sx={{ mt: 2, textAlign: 'right' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Zwischensumme: {orderService.formatTotal({ ...selectedOrder, total: selectedOrder.subtotal })}
                                        </Typography>
                                        {parseFloat(selectedOrder.discount) > 0 && (
                                            <Typography variant="body2" color="success.main">
                                                Rabatt: -{orderService.formatTotal({ ...selectedOrder, total: selectedOrder.discount })}
                                            </Typography>
                                        )}
                                        {parseFloat(selectedOrder.tax) > 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                MwSt.: {orderService.formatTotal({ ...selectedOrder, total: selectedOrder.tax })}
                                            </Typography>
                                        )}
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="h6">
                                            Gesamtsumme: {orderService.formatTotal(selectedOrder)}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Bestellung vom {orderService.formatDate(selectedOrder.createdAt)}
                                        {selectedOrder.paidAt && ` | Bezahlt am ${orderService.formatDate(selectedOrder.paidAt)}`}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>
                                {t('ra.action.close')}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default MyOrders;
